/**
 * Learning Center API: the SOP library (plan docs/plans/lms-plan.md Appendix
 * A "SOPs"; 5.6, 11, 18.25, 18.27; WP-37's routes, WP-46's client). Typed
 * calls over services/api (the shared axios instance with the JWT and
 * X-Site-Id); nothing under src/lms imports axios (2.3a).
 *
 * Learner routes (the caller's network only; a learner sees active SOPs):
 *   GET /sops?status=&course_id=&q=&limit=&offset=   -> { items, page }
 *        items carry effective_version, my_training { assignment, acknowledged_at }
 *        and, for an lms admin only, training { sent_to, acknowledged, pending } (18.25)
 *   GET /sops/by-course/:courseId?course_version_id= -> { sop, version | null, url_path | null }
 *        (the player's "Open the SOP", 11.3, 18.25, 18.27); 404 for a course with no SOP
 *   GET /sops/:id                                    -> { sop, versions }
 *        (an admin gets the version tracker: uploader, sha256, the training version,
 *         the campaign and the counts; a learner the versions they may open)
 *   GET /sops/:id/versions/:vid/url?disposition=     -> { url, filename } (presigned, inline, 10 minutes)
 * Admin routes (requireLmsAdmin):
 *   POST  /admin/sops {sop_number, title, category?, owner_user_id?}           -> 201 sop (409 { existing_sop })
 *   PATCH /admin/sops/:id {title?, category?, owner_user_id?}                  -> sop
 *   POST  /admin/sops/:id/versions (multipart: file, version_label, effective_on?, change_summary?) -> 201 version
 *   POST  /admin/sops/:id/versions/:vid/make-effective                         -> version + previous_effective_version_id
 *   POST  /admin/sops/:id/versions/:vid/send-for-training {mode, retrain_policy, retrain_due_days,
 *         validity_months, audience, due_days, message?, force?, exclude_user_ids?, change_summary?,
 *         estimated_minutes?, notify?, preview?} -> { course, version, campaign | null, outcomes, publish, sop_version }
 *         (preview: true -> 200 { preview, recipients, count }, nothing written: the first send's
 *          recipient preview, 409 once a training version is published; notify: false sends without
 *          the campaign notices; docs/plans/sop-import-plan.md 4.4 items 5 and 6)
 *   GET   /admin/sops/:id/versions/:vid/acknowledgements -> { sop, version, campaign, acknowledged, pending, counts }
 *         (the version carries its label and effective_on; each acknowledgement its expires_at,
 *          current and reauth; sop-import-plan 4.4 item 4)
 *   POST  /admin/sops/:id/retire {reason}                                      -> sop
 *   GET   /admin/reports/sops/:id/versions/:vid/acknowledgements.csv          -> text/csv (WP-39's route)
 *
 * The send (18.25): the mode is the course kind (the first send fixes it; a
 * mismatch is 409 { course_kind }); a quiz-mode send leaves a DRAFT for the
 * course editor and stores no audience, so the audience is sent again, with
 * the same route, once the draft is published. A version already sent is 409
 * { campaign_id, course_version_id }: more people are reached from the Assign
 * wizard (kind sop_update).
 *
 * The acknowledgement counts (18.27) can overlap: a person with an expired
 * acknowledgement and an open renewal is in BOTH lists.
 *
 * Query strings travel as axios `params`, so the URL stays the path (the test
 * harness routes GETs by exact URL).
 */
import api from '../../services/api';
import type { RetrainPolicy } from './catalog';
import type { CampaignAudience, CampaignOutcomeCounts, CampaignPreview, LmsCampaign } from './campaigns';
import type { LmsList } from './client';
import { filenameFromDisposition, readBlobError } from './certificates';

const enc = encodeURIComponent;

// ---------------------------------------------------------------------------
// Bounds (services/lms/sops/validators.js; 5.6; 2.3a)
// ---------------------------------------------------------------------------

export const SOP_NUMBER_MAX = 40;
export const SOP_TITLE_MAX = 200;
export const SOP_CATEGORY_MAX = 80;
export const SOP_VERSION_LABEL_MAX = 40;
export const SOP_CHANGE_SUMMARY_MAX = 4000;
export const SOP_REASON_MAX = 500;
/** 2.3a: SOP uploads up to 25 MB. */
export const SOP_FILE_MAX_BYTES = 25 * 1024 * 1024;
/** 5.6: the two file types an SOP version may be. */
export const SOP_PDF_MIME = 'application/pdf';
export const SOP_DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
export const SOP_FILE_MIMES: readonly string[] = [SOP_PDF_MIME, SOP_DOCX_MIME];
/** 11.2: validity_months from the dialog, default 24 (the Appendix E range 1 to 120). */
export const SOP_DEFAULT_VALIDITY_MONTHS = 24;
export const SOP_VALIDITY_MONTHS_MIN = 1;
export const SOP_VALIDITY_MONTHS_MAX = 120;
/** Appendix E estimated_minutes range; the server defaults to the lint's estimate (5 minutes). */
export const SOP_ESTIMATED_MINUTES_MIN = 1;
export const SOP_ESTIMATED_MINUTES_MAX = 600;
/** Appendix A: a list page holds at most 200 rows. */
export const SOPS_MAX_LIMIT = 200;

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

/** 5.6 lms_sops.status. */
export type SopStatus = 'active' | 'retired';

/** 5.6 lms_sop_versions.status. */
export type SopVersionStatus = 'draft' | 'effective' | 'superseded' | 'retired';

/** 11.2 step 1: Read and acknowledge (the default) or Quiz; it is the course kind. */
export type SopSendMode = 'read_ack' | 'quiz';

/** The SOP row as every view carries it (services/lms/sops/access.js sopBaseView). */
export interface SopRow {
  id: string;
  network_id: string;
  sop_number: string;
  title: string;
  category: string | null;
  owner_user_id: string | null;
  status: SopStatus;
  current_version_id: string | null;
  course_id: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/** An SOP version as a learner sees it (no uploader, hash or storage key). */
export interface SopVersion {
  id: string;
  sop_id: string;
  version_label: string;
  status: SopVersionStatus;
  effective_on: string | null;
  filename: string;
  mime: string;
  size_bytes: number;
  change_summary: string | null;
  uploaded_at: string | null;
  made_effective_at: string | null;
  superseded_at: string | null;
  retired_at: string | null;
  course_version_id: string | null;
}

/** An SOP version as the admin tracker shows it (11.1): everything but the storage key. */
export interface SopAdminVersion extends SopVersion {
  sha256: string;
  uploaded_by: string | null;
  made_effective_by: string | null;
  sent_campaign_id: string | null;
  created_at: string | null;
}

/** 11.1 "sent to n, acknowledged m, pending k" over the people the admin reaches; the two counts can overlap (18.27). */
export interface SopTrainingCounts {
  sent_to: number | null;
  acknowledged: number;
  pending: number;
}

/** The caller's own training on the SOP's effective version (18.25). */
export interface SopMyTraining {
  assignment: { id: string; due_at: string; mode: 'full' | 'acknowledge'; required_version_id: string | null } | null;
  acknowledged_at: string | null;
}

/** One GET /sops item. */
export interface SopListItem extends SopRow {
  owner: { id: string; full_name: string | null } | null;
  effective_version: (SopVersion & Partial<SopAdminVersion>) | null;
  my_training: SopMyTraining;
  /** Admins only: the effective version's training, or null when it was never sent. */
  training?: SopTrainingCounts | null;
}

/** The SOP's training course as the detail carries it (11.2: created on the first send). */
export interface SopCourseRef {
  id: string;
  slug: string;
  title: string;
  kind: SopSendMode;
  is_active: boolean;
  current_version_id: string | null;
}

/** GET /sops/:id sop. */
export interface SopDetailSop extends SopRow {
  owner: { id: string; full_name: string | null } | null;
  course: SopCourseRef | null;
  my_training: SopMyTraining;
}

/** The training version linked to an SOP version (tracker only). */
export interface SopCourseVersionRef {
  id: string;
  version_no: number;
  status: 'draft' | 'published' | 'superseded' | 'retired';
  retrain_policy: RetrainPolicy | null;
  published_at: string | null;
}

/** The campaign that sent an SOP version (tracker only). */
export interface SopCampaignRef {
  id: string;
  kind?: string;
  sent_at: string;
  due_at: string;
  recipient_count: number;
}

/** A version in GET /sops/:id: the admin tracker's fields are present for an lms admin only. */
export interface SopDetailVersion extends SopVersion, Partial<Omit<SopAdminVersion, keyof SopVersion>> {
  uploaded_by_name?: string | null;
  made_effective_by_name?: string | null;
  course_version?: SopCourseVersionRef | null;
  campaign?: SopCampaignRef | null;
  training?: SopTrainingCounts | null;
}

export interface SopDetail {
  sop: SopDetailSop;
  /** Newest first. */
  versions: SopDetailVersion[];
}

/** GET /sops/by-course/:courseId (18.25). */
export interface SopForCourse {
  sop: { id: string; sop_number: string; title: string; status: SopStatus; course_id: string };
  version: SopVersion | null;
  /** The route that mints the presigned URL, relative to /api/lms; null when no version can be opened. */
  url_path: string | null;
}

/** A presigned URL answer (2.3a "Downloads"). */
export interface SopFileUrl {
  url: string;
  filename: string;
}

export interface CreateSopInput {
  sop_number: string;
  title: string;
  category?: string | null;
  owner_user_id?: string | null;
}

export interface UpdateSopInput {
  title?: string;
  category?: string | null;
  owner_user_id?: string | null;
}

export interface UploadSopVersionInput {
  file: File;
  version_label: string;
  effective_on?: string | null;
  change_summary?: string | null;
}

/** POST .../make-effective answers the version and the one it superseded. */
export interface MadeEffective extends SopAdminVersion {
  previous_effective_version_id: string | null;
}

/**
 * The send-for-training body (Appendix A plus 18.25's force,
 * exclude_user_ids, change_summary and estimated_minutes). Every field but
 * mode is optional on the wire; the dialog decides which to send.
 */
export interface SendSopForTrainingInput {
  mode: SopSendMode;
  retrain_policy?: RetrainPolicy;
  retrain_due_days?: number;
  validity_months?: number | null;
  audience?: CampaignAudience;
  due_days?: number;
  message?: string;
  force?: boolean;
  exclude_user_ids?: string[];
  change_summary?: string;
  estimated_minutes?: number;
  /** false: send without the 'campaign' notices (the daily reminders still follow). Default true. */
  notify?: boolean;
}

/** The preview body: what the send would carry, with preview: true (sop-import-plan 4.4 item 6). */
export type SopSendPreviewInput = Omit<SendSopForTrainingInput, 'notify'> & { audience: CampaignAudience };

/** POST .../send-for-training with preview: true: the campaign preview's shape, nothing written. */
export interface SopSendPreview extends CampaignPreview {
  preview: true;
}

export interface SendSopForTrainingResult {
  course: {
    id: string;
    slug: string;
    title: string;
    category: string;
    kind: SopSendMode;
    validity_months: number | null;
    renewal_lead_days: number | null;
    is_active: boolean;
    current_version_id: string | null;
    sop_id: string | null;
  };
  version: {
    id: string;
    course_id: string;
    version_no: number;
    status: 'draft' | 'published' | 'superseded' | 'retired';
    retrain_policy: RetrainPolicy | null;
    change_summary: string | null;
    estimated_minutes: number | null;
    published_at: string | null;
  };
  /** null for a quiz-mode send: the draft is the admin's to finish in the course editor. */
  campaign: LmsCampaign | null;
  outcomes: CampaignOutcomeCounts | null;
  publish: { retrain_campaign: unknown; superseded_completions: number; resumed?: boolean } | null;
  sop_version: SopAdminVersion;
}

export interface SopAckPerson {
  id: string;
  full_name: string | null;
  email: string | null;
  deleted: boolean;
}

export interface SopAcknowledged {
  user: SopAckPerson;
  completion_id: string;
  completed_at: string;
  kind: 'read_ack' | 'quiz';
  superseded: boolean;
  /** When this acknowledgement lapses (null: never); current while neither superseded nor expired. */
  expires_at?: string | null;
  current?: boolean;
  /** Signed with the password re-check (D8). */
  reauth?: boolean;
}

export interface SopPending {
  user: SopAckPerson;
  assignment_id: string;
  due_at: string;
  overdue: boolean;
  source: string;
  mode: 'full' | 'acknowledge';
}

/** GET /admin/sops/:id/versions/:vid/acknowledgements (11.3, 18.25, 18.27). */
export interface SopAcknowledgements {
  sop: { id: string; sop_number: string; title: string; status: SopStatus };
  version: { id: string; version_label: string; effective_on?: string | null; status: SopVersionStatus; course_version_id: string | null };
  campaign: { id: string; sent_at: string; due_at: string; recipient_count: number } | null;
  acknowledged: SopAcknowledged[];
  pending: SopPending[];
  counts: SopTrainingCounts;
}

export interface SopCsvFile {
  blob: Blob;
  filename: string;
}

export interface ListSopsQuery {
  status?: SopStatus | null;
  q?: string | null;
  courseId?: string | null;
  limit?: number;
  offset?: number;
}

// ---------------------------------------------------------------------------
// Learner calls
// ---------------------------------------------------------------------------

/** GET /api/lms/sops (by SOP number; a learner gets active SOPs only). */
export async function listSops(query: ListSopsQuery = {}): Promise<LmsList<SopListItem>> {
  const params: Record<string, string | number> = {};
  if (query.status) params.status = query.status;
  if (query.q) params.q = query.q;
  if (query.courseId) params.course_id = query.courseId;
  params.limit = Math.min(query.limit ?? SOPS_MAX_LIMIT, SOPS_MAX_LIMIT);
  if (query.offset) params.offset = query.offset;
  const response = await api.get<LmsList<SopListItem>>('/api/lms/sops', { params });
  return response.data;
}

/** Guard for a list that is walked to its last page. */
const MAX_PAGES = 25;

/** Every SOP the caller may see (GET /sops read page by page to the last one, at most 5000 rows). */
export async function listAllSops(query: Omit<ListSopsQuery, 'limit' | 'offset'> = {}): Promise<SopListItem[]> {
  const out: SopListItem[] = [];
  let offset = 0;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const answer = await listSops({ ...query, limit: SOPS_MAX_LIMIT, offset });
    const items = Array.isArray(answer?.items) ? answer.items : [];
    out.push(...items);
    if (!answer?.page?.hasMore || items.length === 0) break;
    offset += items.length;
  }
  return out;
}

/** GET /api/lms/sops/:id (the admin version tracker, or a learner's openable versions). */
export async function getSop(sopId: string): Promise<SopDetail> {
  const response = await api.get<SopDetail>(`/api/lms/sops/${enc(sopId)}`);
  return response.data;
}

/** GET /api/lms/sops/:id/versions/:vid/url (inline by default, 11.1; 10 minutes). */
export async function getSopVersionUrl(
  sopId: string,
  versionId: string,
  disposition: 'inline' | 'attachment' = 'inline'
): Promise<SopFileUrl> {
  const response = await api.get<SopFileUrl>(`/api/lms/sops/${enc(sopId)}/versions/${enc(versionId)}/url`, {
    params: { disposition },
  });
  return response.data;
}

/**
 * GET /api/lms/sops/by-course/:courseId?course_version_id= (18.25): the SOP an
 * SOP course trains on and the SOP version to open for the version shown.
 * The one client of the route: the player's Open the SOP button
 * (tabs/sops/OpenSopButton.tsx) and the course page's SOP facts
 * (tabs/courses/CoursePage.tsx; WP-42's tabs/courses/courseSop.ts copy was
 * merged into this function by WP-71, plan 18.29).
 */
export async function getSopForCourse(courseId: string, courseVersionId?: string | null): Promise<SopForCourse> {
  const config = courseVersionId ? { params: { course_version_id: courseVersionId } } : undefined;
  const response = await api.get<SopForCourse>(`/api/lms/sops/by-course/${enc(courseId)}`, config);
  return response.data;
}

/** The only shape a by-course url_path may take (18.25): /sops/<id>/versions/<id>/url. */
const URL_PATH_RE = /^\/sops\/[0-9A-Za-z-]+\/versions\/[0-9A-Za-z-]+\/url$/;

/**
 * The version-URL call for a by-course answer's url_path (18.25: the player
 * GETs /api/lms<url_path>). Anything but the one route shape is refused, so a
 * path can never reach another endpoint.
 */
export async function getSopFileUrlByPath(urlPath: string): Promise<SopFileUrl> {
  if (typeof urlPath !== 'string' || !URL_PATH_RE.test(urlPath)) {
    throw new Error('The SOP file link is not recognised.');
  }
  const response = await api.get<SopFileUrl>(`/api/lms${urlPath}`);
  return response.data;
}

// ---------------------------------------------------------------------------
// Admin calls
// ---------------------------------------------------------------------------

/** POST /api/lms/admin/sops (11.1); category and owner are sent only when set. */
export async function createSop(input: CreateSopInput): Promise<SopRow> {
  const body: Record<string, unknown> = { sop_number: input.sop_number.trim(), title: input.title.trim() };
  const category = (input.category ?? '').trim();
  if (category) body.category = category;
  if (input.owner_user_id) body.owner_user_id = input.owner_user_id;
  const response = await api.post<SopRow>('/api/lms/admin/sops', body);
  return response.data;
}

/** PATCH /api/lms/admin/sops/:id (title, category, owner; the number is fixed once created). */
export async function updateSop(sopId: string, patch: UpdateSopInput): Promise<SopRow> {
  const response = await api.patch<SopRow>(`/api/lms/admin/sops/${enc(sopId)}`, patch);
  return response.data;
}

/** The multipart body of an SOP version upload (11.1): the file and its fields; empty optionals are left out. */
export function buildSopVersionForm(input: UploadSopVersionInput): FormData {
  const form = new FormData();
  form.append('file', input.file);
  form.append('version_label', input.version_label.trim());
  if (input.effective_on) form.append('effective_on', input.effective_on);
  const summary = (input.change_summary ?? '').trim();
  if (summary) form.append('change_summary', summary);
  return form;
}

/**
 * POST /api/lms/admin/sops/:id/versions (multipart). The FormData is posted
 * with no explicit Content-Type: the browser sets the multipart boundary.
 */
export async function uploadSopVersion(sopId: string, input: UploadSopVersionInput): Promise<SopAdminVersion> {
  const response = await api.post<SopAdminVersion>(`/api/lms/admin/sops/${enc(sopId)}/versions`, buildSopVersionForm(input));
  return response.data;
}

/** POST /api/lms/admin/sops/:id/versions/:vid/make-effective (the previous effective version is superseded). */
export async function makeSopVersionEffective(sopId: string, versionId: string): Promise<MadeEffective> {
  const response = await api.post<MadeEffective>(
    `/api/lms/admin/sops/${enc(sopId)}/versions/${enc(versionId)}/make-effective`
  );
  return response.data;
}

/** POST /api/lms/admin/sops/:id/versions/:vid/send-for-training (11.2; the body as the dialog built it). */
export async function sendSopForTraining(
  sopId: string,
  versionId: string,
  input: SendSopForTrainingInput
): Promise<SendSopForTrainingResult> {
  const response = await api.post<SendSopForTrainingResult>(
    `/api/lms/admin/sops/${enc(sopId)}/versions/${enc(versionId)}/send-for-training`,
    input
  );
  return response.data;
}

/**
 * Who a send of this SOP version would reach, writing nothing (POST
 * .../send-for-training with preview: true; sop-import-plan 4.4 item 6). The
 * first send has no course yet, so POST /admin/campaigns/preview cannot
 * answer it; the send's own pre-flight resolves the audience.
 */
export async function previewSopSend(sopId: string, versionId: string, input: SopSendPreviewInput): Promise<SopSendPreview> {
  const response = await api.post<SopSendPreview>(
    `/api/lms/admin/sops/${enc(sopId)}/versions/${enc(versionId)}/send-for-training`,
    { ...input, preview: true }
  );
  return response.data;
}

/** GET /api/lms/admin/sops/:id/versions/:vid/acknowledgements (11.3). */
export async function getSopAcknowledgements(sopId: string, versionId: string): Promise<SopAcknowledgements> {
  const response = await api.get<SopAcknowledgements>(
    `/api/lms/admin/sops/${enc(sopId)}/versions/${enc(versionId)}/acknowledgements`
  );
  return response.data;
}

/** POST /api/lms/admin/sops/:id/retire {reason} (11.1). */
export async function retireSop(sopId: string, reason: string): Promise<SopRow> {
  const response = await api.post<SopRow>(`/api/lms/admin/sops/${enc(sopId)}/retire`, { reason: reason.trim() });
  return response.data;
}

/**
 * GET /api/lms/admin/reports/sops/:id/versions/:vid/acknowledgements.csv
 * (11.3 "with a CSV export"; WP-39's route) as a Blob; the page saves it
 * (api/certificates.ts saveBlob). An error body is parsed back into { error, code }.
 */
export async function downloadSopAcknowledgementsCsv(sopId: string, versionId: string): Promise<SopCsvFile> {
  try {
    const response = await api.get<Blob>(
      `/api/lms/admin/reports/sops/${enc(sopId)}/versions/${enc(versionId)}/acknowledgements.csv`,
      { responseType: 'blob' }
    );
    const headers = (response as { headers?: Record<string, unknown> }).headers || {};
    const data = response.data;
    const blob = data instanceof Blob ? data : new Blob([data as BlobPart], { type: 'text/csv' });
    return {
      blob,
      filename: filenameFromDisposition(headers['content-disposition']) || 'sop-acknowledgements.csv',
    };
  } catch (err) {
    throw await readBlobError(err);
  }
}
